import { useState, useRef, useCallback, useEffect } from "react";
import Button from "../../../components/Button";
import { ShowToast } from "../../../components/toast";
import { useBrand, useBrandSetup, useUpdateHotelBrand } from "../hooks";
import { BRAND_REVIEW_STATE_COLORS } from "../types";
import { validateIconFile } from "../utils";
import { Badge } from "./StatusBadge";

const ICON_PREVIEW_KEY = "metasearch-brand-icon-previews";

function getStoredPreview(brandId: string): string | null {
  try {
    const previews = JSON.parse(localStorage.getItem(ICON_PREVIEW_KEY) || "{}");
    return previews[brandId] || null;
  } catch {
    return null;
  }
}

function storePreview(brandId: string, dataUrl: string) {
  try {
    const previews = JSON.parse(localStorage.getItem(ICON_PREVIEW_KEY) || "{}");
    previews[brandId] = dataUrl;
    localStorage.setItem(ICON_PREVIEW_KEY, JSON.stringify(previews));
  } catch { /* quota */ }
}

function removePreview(brandId: string) {
  try {
    const previews = JSON.parse(localStorage.getItem(ICON_PREVIEW_KEY) || "{}");
    delete previews[brandId];
    localStorage.setItem(ICON_PREVIEW_KEY, JSON.stringify(previews));
  } catch { /* quota */ }
}

function formatReviewState(state: string | undefined): string {
  if (!state) return "—";
  return state.replace("REVIEW_STATE_", "");
}

type Props = {
  configId: string;
  currentBrandId?: string | null;
};

export function BrandCard({ configId, currentBrandId }: Props) {
  const [editing, setEditing] = useState(false);
  const [savedBrandId, setSavedBrandId] = useState<string | null | undefined>(
    undefined
  );
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [iconData, setIconData] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const effectiveBrandId =
    savedBrandId !== undefined ? savedBrandId : currentBrandId;
  const { data: brand } = useBrand(effectiveBrandId ?? null);
  const brandSetup = useBrandSetup();
  const updateHotelBrand = useUpdateHotelBrand();

  // Clean up localStorage preview once Google has approved the icon
  useEffect(() => {
    if (effectiveBrandId && brand?.activeIconUri) {
      removePreview(effectiveBrandId);
    }
  }, [effectiveBrandId, brand?.activeIconUri]);

  const storedPreview = effectiveBrandId ? getStoredPreview(effectiveBrandId) : null;

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      validateIconFile(
        file,
        (base64, previewUrl) => {
          setIconData(base64);
          setIconPreview(previewUrl);
        },
        (message) => ShowToast({ type: "error", message }),
      );
    },
    []
  );

  const clearIcon = () => {
    setIconData(null);
    setIconPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = () => {
    if (!iconData || !iconPreview) return;
    const previewToStore = iconPreview;
    brandSetup.mutate(
      { configId, data: { imageBase64: iconData } },
      {
        onSuccess: (data: any) => {
          const newBrandId = data?.brandId ?? effectiveBrandId;
          if (newBrandId) storePreview(newBrandId, previewToStore);
          setSavedBrandId(newBrandId);
          clearIcon();
          setEditing(false);
        },
      }
    );
  };

  const handleRemoveBrand = () => {
    updateHotelBrand.mutate(
      { configId, brandId: null },
      {
        onSuccess: () => {
          if (effectiveBrandId) removePreview(effectiveBrandId);
          setSavedBrandId(null);
          clearIcon();
          setEditing(false);
        },
      }
    );
  };

  const displayName = brand?.displayNames?.[0]?.text;
  const iconUri = brand?.activeIconUri || storedPreview;
  const iconState = brand?.iconState;
  const nameState = brand?.displayNameState;
  const isPending = brandSetup.isPending || updateHotelBrand.isPending;

  if (!editing) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">Brand</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
          >
            {effectiveBrandId ? "Edit" : "Add Logo"}
          </Button>
        </div>
        {effectiveBrandId && brand ? (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              {iconUri ? (
                <img
                  src={iconUri}
                  alt=""
                  className="w-10 h-10 rounded border border-gray-200 object-contain"
                />
              ) : (
                <div className="w-10 h-10 rounded border border-gray-200 bg-gray-50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="m21 15-5-5L5 21" />
                  </svg>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {displayName || effectiveBrandId}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {iconState && (
                    <Badge
                      className={
                        BRAND_REVIEW_STATE_COLORS[formatReviewState(iconState)] ||
                        "bg-gray-100 text-gray-800 border-gray-300"
                      }
                    >
                      Icon: {formatReviewState(iconState)}
                    </Badge>
                  )}
                  {nameState && (
                    <Badge
                      className={
                        BRAND_REVIEW_STATE_COLORS[formatReviewState(nameState)] ||
                        "bg-gray-100 text-gray-800 border-gray-300"
                      }
                    >
                      Name: {formatReviewState(nameState)}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No brand configured</p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
      <h3 className="text-base font-semibold text-gray-900 mb-3">
        {effectiveBrandId ? "Update Logo" : "Add Logo"}
      </h3>

      {iconPreview ? (
        <div className="flex items-center gap-4 p-3 mb-4 bg-[#FAFAFA] rounded-lg border border-[#E9E9EB]">
          <img
            src={iconPreview}
            alt="Preview"
            className="w-12 h-12 rounded border border-gray-200 object-contain"
          />
          <span className="flex-1 text-sm text-gray-600">New logo ready</span>
          <Button
            variant="outline"
            size="sm"
            onClick={clearIcon}
            disabled={isPending}
          >
            Remove
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors mb-4"
        >
          <svg
            className="w-8 h-8 mx-auto mb-2 text-gray-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="m21 15-5-5L5 21" />
          </svg>
          <span className="text-sm text-gray-500">
            Click to upload a square PNG (72–1200px)
          </span>
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png"
        className="hidden"
        onChange={handleFileSelect}
      />

      <div className="flex items-center gap-2 pt-2">
        <Button
          onClick={handleSave}
          loading={brandSetup.isPending}
          disabled={!iconData || isPending}
        >
          Save
        </Button>
        {effectiveBrandId && (
          <Button
            variant="outline"
            onClick={handleRemoveBrand}
            loading={updateHotelBrand.isPending}
            disabled={isPending}
          >
            Remove Brand
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => {
            clearIcon();
            setEditing(false);
          }}
          disabled={isPending}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
